/**
 * „Twoje tagi" — the manage dialog (canon `F4-zarzadzanie`, 10 October 2026; exercise-library R23).
 *
 * Every tag the trainer holds, against the two hundred they may, with how many movements carry it,
 * and two acts per row: rename, inline, and delete, through the app's one confirmation. Both act on
 * every movement carrying the tag, which is what the sentence under the title says.
 *
 * ── A rename onto a name already in use is named BEFORE it is saved ────────────────────────
 * The two tags become one, in the spelling just typed (R23), and the warning says which tag it
 * merges into and on how many movements the merged tag will stand. The save button says „Scal"
 * rather than „Zapisz", so the act is in the button as well as above it.
 *
 * ── Three states, never a branch on the list ───────────────────────────────────────────────
 * Waiting (gated bars inside the list's box), failed (a sentence and a retry), and answered — and
 * an answer with no tags at all is its own empty card, not an empty list.
 */

import { Dialog } from '@base-ui/react/dialog';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExerciseListItem } from '~/entities/exercise-library';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { CloseGlyph, TrashGlyph } from '~/shared/ui/FormGlyphs';
import surface from '~/shared/ui/dialogSurface.module.css';
import form from '~/shared/ui/formDialog.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import box from '~/shared/ui/listState.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { useTagManager, type TagManagerState } from '../lib/useTagManager';

import { PencilGlyph, TagGlyph } from './ExerciseLibraryIcons';
import tagParts from './tagParts.module.css';
import styles from './TagManagerDialog.module.css';

interface TagManagerDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Every movement — what a merge's figure is counted over. */
  readonly items: readonly ExerciseListItem[];
  /**
   * Where focus goes when the dialog closes — the host's, because only the host knows whether the
   * control that opened it is still there. Deleting the last tag takes the tag bar, „Zarządzaj"
   * with it, and Base UI's default would then return focus to `<body>`.
   */
  readonly finalFocus: () => boolean | HTMLElement | null;
}

/** Bars while the list is on its way: the rows' own height, so the answer changes ink only. */
const SKELETON_ROWS = 4;

export function TagManagerDialog({ open, onClose, items, finalFocus }: TagManagerDialogProps) {
  const { t } = useTranslation();
  const popup = useRef<HTMLDivElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  const manager = useTagManager(open, items, () => {
    close.current?.focus();
  });
  const deleting = manager.deleting;
  /* Where focus goes when the delete prompt closes on its CONFIRM: the trash it was raised from is
   * unmounted with its row, so it is the next row's trash, else the previous one's, else the
   * dialog's close. Null until the confirm is pressed, which `ConfirmDialog`'s Base UI reads as
   * "the default" — a cancel goes back to the trash that is still there. */
  const afterDelete = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          manager.cancelRename();
          // Closing the dialog is not leaving a row: no pencil waits for focus on the next open.
          manager.returnedFocus();
          onClose();
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <Dialog.Popup className={surface.popup} ref={popup} finalFocus={finalFocus}>
          <div className={styles.head}>
            <TagGlyph className={styles.glyph} />
            <Dialog.Title className={surface.title}>{t('web:exerciseLibrary.tags.title')}</Dialog.Title>
            {manager.status === 'ready' && (
              <span className={styles.total}>
                {t('web:exerciseLibrary.tags.countOf', {
                  used: manager.tags.length,
                  max: manager.max,
                })}
              </span>
            )}
            <Dialog.Close
              ref={close}
              className={form.close}
              aria-label={t('web:exerciseLibrary.tags.close')}
            >
              <CloseGlyph className={form.closeGlyph} />
            </Dialog.Close>
          </div>
          <Dialog.Description className={surface.description}>
            {t('web:exerciseLibrary.tags.manageNote')}
          </Dialog.Description>

          <TagManagerBody manager={manager} />

          {/* Always mounted: a status region inserted with its text is not announced. */}
          <p className={styles.notice} role="status">
            {manager.notice === null
              ? ''
              : t(
                  manager.notice === 'invalid'
                    ? 'web:exerciseLibrary.tags.refusal.invalid'
                    : 'web:exerciseLibrary.tags.refusal.failed',
                )}
          </p>

          {/* NESTED in this popup's tree, so Base UI stacks it over the dialog and Escape answers
            * the prompt alone. */}
          <ConfirmDialog
            prompt={
              deleting === null
                ? null
                : {
                    title: t('web:exerciseLibrary.tags.deleteTitle', { tag: deleting.label }),
                    description: t('web:exerciseLibrary.tags.deleteDescription', {
                      count: deleting.exerciseCount,
                    }),
                    confirmLabel: t('web:exerciseLibrary.tags.deleteConfirm'),
                    cancelLabel: t('web:exerciseLibrary.tags.cancel'),
                    destructiveConfirm: true,
                    busy: manager.busy,
                  }
            }
            onConfirm={() => {
              const neighbour = manager.focusAfterDelete;
              afterDelete.current =
                (neighbour === null
                  ? null
                  : popup.current?.querySelector<HTMLElement>(
                      `[data-tag-delete="${CSS.escape(neighbour)}"]`,
                    )) ?? close.current;
              manager.confirmDelete();
            }}
            onCancel={() => {
              afterDelete.current = null;
              manager.cancelDelete();
            }}
            finalFocus={afterDelete}
          />
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function TagManagerBody({ manager }: { readonly manager: TagManagerState }) {
  const { t } = useTranslation();
  const list = useRef<HTMLUListElement>(null);
  const { returnFocusTo, returnedFocus } = manager;
  // A rename row that closed hands focus to its row's pencil — the renamed row's after a save, which
  // is in the list once the re-read has landed.
  useEffect(() => {
    if (returnFocusTo === null) return;
    const pencil = list.current?.querySelector<HTMLElement>(
      `[data-tag-rename="${CSS.escape(returnFocusTo)}"]`,
    );
    if (pencil === null || pencil === undefined) return;
    pencil.focus();
    returnedFocus();
  });

  if (manager.status === 'loading') {
    return (
      <div className={styles.rows} aria-busy="true">
        <p className={hidden.srOnly}>{t('web:exerciseLibrary.tags.loading')}</p>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <div key={index} className={styles.row}>
            {manager.gate.skeleton && <i className={`${String(skel.bar)} ${String(styles.skelLabel)}`} />}
          </div>
        ))}
      </div>
    );
  }

  if (manager.status === 'error') {
    return (
      <div className={`${String(box.state)} ${manager.gate.reveal}`}>
        <EmptyState
          mark={<NoteMarkIcon />}
          tone="neutral"
          title={t('web:exerciseLibrary.tags.errorTitle')}
          hint={t('web:exerciseLibrary.tags.errorHint')}
        />
        <div className={box.actions}>
          <button type="button" className={box.ghost} onClick={manager.retry}>
            {t('web:exerciseLibrary.states.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (manager.tags.length === 0) {
    return (
      <div className={`${String(box.state)} ${manager.gate.reveal}`}>
        <EmptyState
          mark={<NoteMarkIcon />}
          tone="positive"
          title={t('web:exerciseLibrary.tags.emptyTitle')}
          hint={t('web:exerciseLibrary.tags.emptyHint')}
        />
      </div>
    );
  }

  return (
    <ul ref={list} className={`${String(styles.rows)} ${manager.gate.reveal}`}>
      {manager.tags.map((tag) =>
        manager.editing?.label === tag.label ? (
          <RenameRow key={tag.label} manager={manager} editing={manager.editing} />
        ) : (
          <li key={tag.label} className={styles.row}>
            <span className={styles.label}>{tag.label}</span>
            <span className={styles.count}>
              {t('web:exerciseLibrary.tags.rowCount', { count: tag.exerciseCount })}
            </span>
            <span className={styles.acts}>
              <button
                type="button"
                className={styles.iconAct}
                data-tag-rename={tag.label}
                aria-label={t('web:exerciseLibrary.tags.rename', { tag: tag.label })}
                aria-disabled={manager.busy}
                onClick={() => {
                  if (!manager.busy) manager.startRename(tag.label);
                }}
              >
                <PencilGlyph className={styles.iconGlyph} />
              </button>
              <button
                type="button"
                className={styles.iconAct}
                data-tag-delete={tag.label}
                aria-label={t('web:exerciseLibrary.tags.delete', { tag: tag.label })}
                aria-disabled={manager.busy}
                onClick={() => {
                  if (!manager.busy) manager.askDelete(tag);
                }}
              >
                <TrashGlyph className={styles.iconGlyph} />
              </button>
            </span>
          </li>
        ),
      )}
    </ul>
  );
}

function RenameRow({
  manager,
  editing,
}: {
  readonly manager: TagManagerState;
  readonly editing: NonNullable<TagManagerState['editing']>;
}) {
  const { t } = useTranslation();
  const warningId = useId();
  const plan = editing.plan;
  const mergeWarning =
    plan.kind === 'merge'
      ? t('web:exerciseLibrary.tags.mergeWarning', {
          target: plan.target,
          label: plan.label,
          count: plan.count,
        })
      : '';

  return (
    <li className={styles.renameRow}>
      <label className={tagParts.field}>
        <span className={hidden.srOnly}>
          {t('web:exerciseLibrary.tags.renameLabel', { tag: editing.label })}
        </span>
        <input
          className={tagParts.fieldInput}
          value={editing.text}
          maxLength={64}
          // The row opened on the pencil's press, so the field is where focus goes next — the pencil
          // that had it is no longer in the document.
          autoFocus
          aria-describedby={plan.kind === 'merge' ? warningId : undefined}
          onChange={(event) => {
            manager.setRenameText(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              manager.saveRename();
            } else if (event.key === 'Escape') {
              // The row's own Escape, so the dialog stays open behind it.
              event.preventDefault();
              event.stopPropagation();
              manager.cancelRename();
            }
          }}
        />
        <span className={tagParts.fieldHint} aria-hidden>
          {t('web:exerciseLibrary.tags.was', { tag: editing.label })}
        </span>
      </label>
      {/* The merge is SPOKEN when the typed name turns into one: an always-mounted status
        * region, visually hidden — `aria-describedby` alone is read only when focus arrives, and
        * focus is already in the field. Hidden rather than in the row's flow, because an empty
        * box there still took the row's gap. The visible box exists only while there is a merge
        * to name, and is what the field's description points at. */}
      <p className={hidden.srOnly} role="status">
        {mergeWarning}
      </p>
      {mergeWarning !== '' && (
        <p className={styles.warn} id={warningId} aria-hidden>
          {mergeWarning}
        </p>
      )}
      <div className={styles.renameActs}>
        <button type="button" className={surface.cancel} onClick={manager.cancelRename}>
          {t('web:exerciseLibrary.tags.cancel')}
        </button>
        <button
          type="button"
          className={`${String(ink.ink)} ${String(styles.save)}`}
          aria-disabled={manager.busy || plan.kind === 'invalid'}
          onClick={manager.saveRename}
        >
          {plan.kind === 'merge'
            ? t('web:exerciseLibrary.tags.mergeConfirm', { label: plan.label })
            : t('web:exerciseLibrary.tags.save')}
        </button>
      </div>
    </li>
  );
}
